import React, { useState } from "react";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";

import { TOO_MANY_ATTEMPTS, isThrottled, login } from "@/api/auth";
import { useMeQuery } from "@/queries/queries";
import { postLoginTarget } from "@/lib/postLoginHome";
import { Button } from "@/components/shared/ui/button";
import { Input } from "@/components/shared/ui/input";
import { Label } from "@/components/shared/ui/label";

const routeApi = getRouteApi("/login");

const LoginForm = () => {
  const navigate = useNavigate();
  const search = routeApi.useSearch() as { redirect?: string };
  const meQuery = useMeQuery();

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState<boolean>(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    const formData = new FormData(event.currentTarget);
    const formDataObj = Object.fromEntries(formData.entries());
    const email = formDataObj.email as string;
    const password = formDataObj.password as string;

    try {
      await login(email, password);
      // Refetch /me with the new auth token, then route through the one helper the other two
      // login forms use: it drops ?redirect=/ for Tracker-home users (the Classic flash, 10-14).
      const fresh = await meQuery.refetch();
      navigate(postLoginTarget(fresh.data, search?.redirect));
    } catch (err) {
      setErrorMessage(
        isThrottled(err)
          ? TOO_MANY_ATTEMPTS
          : "That email and password don't match. Try again or reset your password.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordCapsLockCheck = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    setIsCapsLockOn(event.getModifierState("CapsLock"));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoFocus
          required
          placeholder="m@example.com"
          className="h-11 text-base"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Password</Label>
          <Link
            to="/external/password_reset_request"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            Forgot?
          </Link>
        </div>
        <Input
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          required
          className="h-11 text-base"
          onKeyDown={handlePasswordCapsLockCheck}
          onKeyUp={handlePasswordCapsLockCheck}
          endIcon={
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-pressed={showPassword}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="flex items-center justify-center text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm transition-colors duration-150 active:scale-90"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          }
        />
        <div className="min-h-[16px]">
          {isCapsLockOn && (
            <p className="text-xs text-muted-foreground animate-in fade-in slide-in-from-top-1 duration-150">
              Caps Lock is on
            </p>
          )}
        </div>
      </div>

      <div className="min-h-[44px]">
        {errorMessage && (
          <div
            role="alert"
            aria-live="polite"
            className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive text-sm animate-in fade-in slide-in-from-top-1 duration-200"
          >
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {errorMessage === TOO_MANY_ATTEMPTS ? (
              <span>{TOO_MANY_ATTEMPTS}</span>
            ) : (
              <span>
                That email and password don't match. Try again or{" "}
                <Link
                  to="/external/password_reset_request"
                  className="underline hover:no-underline transition-all duration-150"
                >
                  reset your password
                </Link>
                .
              </span>
            )}
          </div>
        )}
      </div>

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={isSubmitting}
        aria-busy={isSubmitting}
      >
        {isSubmitting && (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
        )}
        Sign in
      </Button>
    </form>
  );
};

export default LoginForm;
